/*! entrega-estimada.js (Arenaa)
 *  Computes the estimated delivery range in the browser (cache-proof) and keeps it
 *  in sync with variant changes (PDP) and AJAX cart re-renders (cart page / drawer).
 *  All configuration (cut-off, holidays, copy, lead times) comes from data-attributes
 *  rendered by snippets/entrega-estimada.liquid.
 *
 *  QA helpers (add to any URL):  ?ee_now=2026-10-05T12:23  simulates "now" (Madrid time)
 *                                ?ee_debug=1  prints the resolved config of every widget in the console
 */
(function () {
  'use strict';
  if (window.ArenaaEntrega) return;

  var SELECTOR = '[data-entrega-estimada]';
  var PDP_SELECTOR = SELECTOR + '[data-variants]';
  // Generic variant events used by several themes. Harmless if never fired.
  var VARIANT_EVENTS = ['variant:change', 'variant:update', 'variant:selected', 'variantChange', 'product:variant-change'];
  var POLL_MS = 500;    // variant fallback poll (PDP only, paused in background tabs)
  var TICK_MS = 60000;  // re-check once a minute (cut-off / midnight rollover)
  var DAY = 86400000;
  var MAX_DAYS = 120;

  var formatters = {};
  var holidayCache = {};
  var scheduled = false;
  var pollTimer = null;

  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function ymd(t) { return t.getUTCFullYear() + '-' + pad(t.getUTCMonth() + 1) + '-' + pad(t.getUTCDate()); }
  function ddmm(t) { return pad(t.getUTCDate()) + '/' + pad(t.getUTCMonth() + 1); }
  function toInt(v, fallback) {
    var n = parseInt(v, 10);
    return isNaN(n) || n < 0 ? fallback : Math.min(n, MAX_DAYS);
  }

  // "Now" in the store's time zone (not the visitor's), so the cut-off is the same for everyone.
  function zonedNow(tz) {
    var q = /[?&]ee_now=(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(window.location.search);
    if (q) return { y: +q[1], m: +q[2], d: +q[3], mins: +q[4] * 60 + +q[5] };
    try {
      var f = formatters[tz] || (formatters[tz] = new Intl.DateTimeFormat('en-GB', {
        timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
      }));
      var o = {};
      f.formatToParts(new Date()).forEach(function (p) { o[p.type] = p.value; });
      return { y: +o.year, m: +o.month, d: +o.day, mins: (+o.hour % 24) * 60 + +o.minute };
    } catch (e) {
      var n = new Date();
      return { y: n.getFullYear(), m: n.getMonth() + 1, d: n.getDate(), mins: n.getHours() * 60 + n.getMinutes() };
    }
  }

  // The client types holidays in a free-text box. Accepted, one or many per line, any extra text ignored:
  //   2026-12-25            25/12/2026            25/12/2026 Navidad
  //   2026-08-10 a 2026-08-21     10/08/2026 al 21/08/2026     10/08/2026 - 21/08/2026   (closure range)
  //   also "..", "–", "—", "→", "hasta", "to" between the two dates. dd/mm/yyyy is day first.
  var DATE_PART = '(?:\\d{4}-\\d{2}-\\d{2}|\\d{1,2}/\\d{1,2}/\\d{4})';
  var HOLIDAY_SRC = '(' + DATE_PART + ')(?:(?:\\s*(?:\\.\\.|–|—|→)\\s*|\\s+(?:-|a|al|hasta|to)\\s+)(' + DATE_PART + '))?';

  function toUTC(str) {
    var y, mo, d, m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
    if (m) { y = +m[1]; mo = +m[2]; d = +m[3]; }
    else {
      m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(str);
      if (!m) return null;
      d = +m[1]; mo = +m[2]; y = +m[3];
    }
    var t = new Date(Date.UTC(y, mo - 1, d));
    return t.getUTCMonth() === mo - 1 ? t : null; // rejects impossible dates such as 31/02
  }

  function parseHolidays(str) {
    str = str || '';
    if (holidayCache[str]) return holidayCache[str];
    var map = {}, re = new RegExp(HOLIDAY_SRC, 'g'), m;
    while ((m = re.exec(str))) {
      var a = toUTC(m[1]), b = m[2] ? toUTC(m[2]) : a;
      if (!a || !b) continue;
      if (b < a) { var tmp = a; a = b; b = tmp; }
      for (var t = a, i = 0; t <= b && i < 400; t = new Date(t.getTime() + DAY), i++) map[ymd(t)] = true;
    }
    return (holidayCache[str] = map);
  }

  // "14:00", "14.30", "14h", "14h30" and "14" are all accepted.
  function parseCutoff(str) {
    var m = /^(\d{1,2})(?:[:.hH](\d{2})?)?$/.exec((str || '').trim());
    return m && +m[1] <= 24 ? +m[1] * 60 + (m[2] ? +m[2] : 0) : 14 * 60;
  }

  // All date maths is done on UTC midnights, so DST can never shift a day.
  function isBusinessDay(t, holidays) {
    var wd = t.getUTCDay();
    return wd !== 0 && wd !== 6 && !holidays[ymd(t)];
  }
  function nextBusinessDay(t, holidays) {
    var next = new Date(t.getTime() + DAY), guard = 0;
    while (!isBusinessDay(next, holidays) && guard++ < 400) next = new Date(next.getTime() + DAY);
    return next;
  }
  function addBusinessDays(t, n, holidays) {
    var r = t;
    for (var i = 0; i < n; i++) r = nextBusinessDay(r, holidays);
    return r;
  }

  // Day 0 = today if it is a business day and before the cut-off, otherwise the next business day.
  function estimate(now, cutoffMins, holidays, minDays, maxDays) {
    var today = new Date(Date.UTC(now.y, now.m - 1, now.d));
    var base = isBusinessDay(today, holidays) && now.mins < cutoffMins ? today : nextBusinessDay(today, holidays);
    return { from: addBusinessDays(base, minDays, holidays), to: addBusinessDays(base, maxDays, holidays) };
  }

  function update(el) {
    // Theme re-rendered/morphed the node with a new server-side variant? Re-seed.
    var attrV = el.getAttribute('data-variant');
    if (el._eeAttrV !== attrV) { el._eeAttrV = attrV; el._eeVariant = attrV; }

    var state = el.getAttribute('data-state') || 'in';
    if (el._eeMap === undefined) {
      try { el._eeMap = JSON.parse(el.getAttribute('data-variants') || 'null'); } catch (e) { el._eeMap = null; }
    }
    // The map only lists non-"in" variants; anything not listed is in stock.
    if (el._eeMap) state = el._eeMap[el._eeVariant] || 'in';

    if (state === 'none') { el.hidden = true; return; }
    el.hidden = false;

    var pre = state === 'pre';

    // --- NEW: Fixed Date Override for Pre-orders ---
    var preDate = el.getAttribute('data-pre-date');
    if (pre && preDate) {
      var parts = preDate.split('-'); // Shopify outputs YYYY-MM-DD
      if (parts.length === 3) {
        var dateStr = parts[2] + '/' + parts[1]; // Convert to DD/MM
        var tplDate = el.getAttribute('data-tpl-pre-date') || '';
        var fixedText = tplDate.replace('{date}', dateStr);
        
        var textElDate = el.querySelector('.entrega-estimada__text');
        if (el.getAttribute('data-ee-state') !== state) el.setAttribute('data-ee-state', state);
        if (textElDate && textElDate.textContent !== fixedText) textElDate.textContent = fixedText;
        
        return; // Exit early! Skips all the complex business day math below.
      }
    }
    // -----------------------------------------------
    var min = toInt(el.getAttribute(pre ? 'data-pre-min' : 'data-in-min'), 0);
    var max = Math.max(min, toInt(el.getAttribute(pre ? 'data-pre-max' : 'data-in-max'), min));
    var est = estimate(
      zonedNow(el.getAttribute('data-tz') || 'Europe/Madrid'),
      parseCutoff(el.getAttribute('data-cutoff')),
      parseHolidays(el.getAttribute('data-holidays')),
      min, max
    );
    var from = ddmm(est.from), to = ddmm(est.to);
    var tpl = el.getAttribute('data-tpl-' + (pre ? 'pre' : 'in') + (from === to ? '-single' : '')) || '';
    var text = tpl.replace('{from}', from).replace('{to}', to);
    if (!text) return;

    var textEl = el.querySelector('.entrega-estimada__text');
    if (el.getAttribute('data-ee-state') !== state) el.setAttribute('data-ee-state', state);
    if (textEl && textEl.textContent !== text) textEl.textContent = text; // write only on change
  }

  function scanAll() {
    var els = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < els.length; i++) update(els[i]);
    if (!pollTimer && document.querySelector(PDP_SELECTOR)) {
      pollTimer = setInterval(function () {
        if (document.hidden) return;
        if (!document.querySelector(PDP_SELECTOR)) { clearInterval(pollTimer); pollTimer = null; return; }
        syncVariants();
      }, POLL_MS);
    }
  }

  // ---- Variant handling (product page) ----
  function readVariantId(el) {
    var scope = el.closest('.shopify-section') || document;
    var inputs = scope.querySelectorAll('form[action*="/cart/add"] [name="id"]');
    for (var i = 0; i < inputs.length; i++) {
      var inp = inputs[i];
      if (inp.type === 'radio' || inp.type === 'checkbox') { if (inp.checked) return String(inp.value); }
      else if (inp.value) return String(inp.value);
    }
    var m = /[?&]variant=(\d+)/.exec(window.location.search);
    return m ? m[1] : null;
  }

  function syncVariants() {
    var els = document.querySelectorAll(PDP_SELECTOR);
    for (var i = 0; i < els.length; i++) {
      var id = readVariantId(els[i]);
      if (id) els[i]._eeVariant = id;
      update(els[i]); // cheap, and only touches the DOM when the text changes
    }
  }

  function onThemeVariantEvent() {
    syncVariants();
    setTimeout(syncVariants, 120); // some themes update the form a tick after the event
  }

  // ---- AJAX cart: re-run when widgets are (re)inserted ----
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    // rAF runs before the next paint, so new nodes are fixed before the user sees them.
    (window.requestAnimationFrame || window.setTimeout)(function () { scheduled = false; scanAll(); });
  }

  function onMutations(mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType === 1 && ((n.matches && n.matches(SELECTOR)) || (n.querySelector && n.querySelector(SELECTOR)))) {
          schedule();
          return;
        }
      }
    }
  }

  function init() {
    scanAll();

    // childList only (no attributes/characterData): our own text updates never retrigger it.
    new MutationObserver(onMutations).observe(document.body, { childList: true, subtree: true });

    // Variant hooks, layered: (1) theme events, (2) Dawn pub/sub, (3) URL changes, (4) poll above.
    VARIANT_EVENTS.forEach(function (name) { document.addEventListener(name, onThemeVariantEvent); });
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        subscribe(PUB_SUB_EVENTS.variantChange, onThemeVariantEvent);
      }
    } catch (e) { /* not a Dawn-based theme */ }
    ['pushState', 'replaceState'].forEach(function (m) {
      var orig = window.history[m];
      window.history[m] = function () {
        var r = orig.apply(this, arguments);
        setTimeout(syncVariants, 100);
        return r;
      };
    });
    window.addEventListener('popstate', onThemeVariantEvent);

    setInterval(function () { if (!document.hidden) scanAll(); }, TICK_MS);
    document.addEventListener('visibilitychange', function () { if (!document.hidden) scanAll(); });
  }

  // Troubleshooting: add ?ee_debug=1 to a URL (or run ArenaaEntrega.debug() in the console).
  function debugDump() {
    var els = document.querySelectorAll(SELECTOR), rows = [];
    for (var i = 0; i < els.length; i++) {
      var el = els[i], t = el.querySelector('.entrega-estimada__text');
      rows.push({
        'config source': el.getAttribute('data-ee-src'),
        state: el.getAttribute('data-ee-state'),
        cutoff: el.getAttribute('data-cutoff'),
        'holiday days': Object.keys(parseHolidays(el.getAttribute('data-holidays'))).length,
        'in stock (min-max)': el.getAttribute('data-in-min') + '-' + el.getAttribute('data-in-max'),
        'pre-order (min-max)': el.getAttribute('data-pre-min') + '-' + el.getAttribute('data-pre-max'),
        text: t ? t.textContent : ''
      });
    }
    if (window.console && console.table) console.table(rows); else if (window.console) console.log(rows);
    return rows.length + ' widget(s)';
  }

  window.ArenaaEntrega = { refresh: scanAll, sync: syncVariants, update: update, debug: debugDump };

  function start() {
    init();
    if (/[?&]ee_debug=1/.test(window.location.search)) setTimeout(debugDump, 300);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();